import { baseApi, Http, Cache } from '@app/base';
import { selectContext } from './usersApi';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { debounce } from 'lodash';
import {
  DashboardLayoutItem,
  SaveLanguageSettingsRequest,
  SaveTableSettingsRequest,
  UiSettings,
} from './uiApiTypes';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useMobile, type TableSettingsValue } from '@app/ui';

const INITIAL_UI_SETTINGS: UiSettings = {
  table: {},
  dashboard: {},
  mobileDashboard: {},
  language: import.meta.env.VITE_DEFAULT_LANGUAGE,
};

const SECONDS_IN_30_DAYS = 30 * 24 * 60 * 60;
let cache: Cache;

function getOrCreateCache(getState: BaseQueryApi['getState']) {
  const id = selectContext({ getState }).id;

  if (!cache) {
    cache = new Cache({ key: 'ui/' + id, ttlSeconds: SECONDS_IN_30_DAYS });
  }

  return cache;
}

function getCache(key: string, getState: BaseQueryApi['getState']) {
  return getOrCreateCache(getState).get<UiSettings>(key);
}

function setCache(key: string, value: any, getState: BaseQueryApi['getState']) {
  return getOrCreateCache(getState).set(key, value);
}

const put = debounce(async (value: UiSettings) => {
  await Http.axios().put(`/api/ui/record/settings`, value);
}, 500);

function updateSettingsCache(
  queryFulfilled: Promise<{ data: UiSettings }>,
  dispatch: BaseQueryApi['dispatch'],
  getState: BaseQueryApi['getState'],
) {
  queryFulfilled.then(({ data }) => {
    setCache('settings', data, getState);
    dispatch(uiApi.util.updateQueryData('getUiSettings', undefined, () => data));
  });
}

export const uiApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUiSettings: build.query<UiSettings, void>({
      queryFn: async (_, { getState }) => {
        const data: UiSettings = await getOrCreateCache(getState).getOrCreate('settings', () =>
          Http.axios()
            .get(`/api/ui/record/settings`)
            .then((x: any) => x.data?.value ?? INITIAL_UI_SETTINGS),
        );

        return { data };
      },

      keepUnusedDataFor: SECONDS_IN_30_DAYS,
    }),

    saveTableSettings: build.mutation<UiSettings, SaveTableSettingsRequest>({
      queryFn: async ({ uid, value }, { getState }) => {
        const settings = getCache('settings', getState) ?? INITIAL_UI_SETTINGS;
        const newSettings = { ...settings, table: { ...settings.table, [uid]: value } };
        put(newSettings);
        return { data: newSettings };
      },
      onQueryStarted: (_, { dispatch, getState, queryFulfilled }) => {
        updateSettingsCache(queryFulfilled, dispatch, getState);
      },
    }),

    saveLanguageSettings: build.mutation<UiSettings, SaveLanguageSettingsRequest>({
      queryFn: async ({ language }, { getState }) => {
        const cached = getCache('settings', getState);

        if (cached?.language === language) {
          return { data: cached };
        }

        const settings = cached ?? INITIAL_UI_SETTINGS;
        const newSettings = { ...settings, language };
        put(newSettings);
        return { data: newSettings };
      },
      onQueryStarted: (_, { dispatch, getState, queryFulfilled }) => {
        updateSettingsCache(queryFulfilled, dispatch, getState);
      },
    }),

    saveDesktopDashboardLayout: build.mutation<UiSettings, { uid: string; value: DashboardLayoutItem[] }>({
      queryFn: async ({ uid, value }, { getState }) => {
        const settings = getCache('settings', getState) ?? INITIAL_UI_SETTINGS;
        const newSettings = {
          ...settings,
          dashboard: {
            ...settings.dashboard,
            [uid]: value,
          },
        };
        put(newSettings);
        return { data: newSettings };
      },
      onQueryStarted: (_, { dispatch, getState, queryFulfilled }) => {
        updateSettingsCache(queryFulfilled, dispatch, getState);
      },
    }),

    saveMobileDashboardLayout: build.mutation<UiSettings, { uid: string; value: DashboardLayoutItem[] }>({
      queryFn: async ({ uid, value }, { getState }) => {
        const settings = getCache('settings', getState) ?? INITIAL_UI_SETTINGS;
        const newSettings = {
          ...settings,
          mobileDashboard: {
            ...settings.mobileDashboard,
            [uid]: value,
          },
        };
        put(newSettings);
        return { data: newSettings };
      },
      onQueryStarted: (_, { dispatch, getState, queryFulfilled }) => {
        updateSettingsCache(queryFulfilled, dispatch, getState);
      },
    }),
  }),
});

export function useSaveLanguageSettings() {
  const { i18n } = useTranslation();
  const [saveLanguage] = useSaveLanguageSettingsMutation();

  useEffect(() => {
    i18n.language && saveLanguage({ language: i18n.language });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
}

export function useTableSettingsSave() {
  const [saveTableSettings] = useSaveTableSettingsMutation();

  return (uid: string, settings: TableSettingsValue) => {
    saveTableSettings({ uid, value: settings });
  };
}

export function useTableSettingsValue(uid: string) {
  const { data } = useGetUiSettingsQuery();

  return useMemo(
    () => ({
      hiddenColumns: data?.table[uid]?.hiddenColumns || [],
    }),
    [data, uid],
  );
}

export function useDashboardLayoutSave() {
  const { isMobile } = useMobile();
  const [saveDesktop] = useSaveDesktopDashboardLayoutMutation();
  const [saveMobile] = useSaveMobileDashboardLayoutMutation();

  return (uid: string, layout: DashboardLayoutItem[]) => {
    if (isMobile) {
      saveMobile({ uid, value: layout });
    } else {
      saveDesktop({ uid, value: layout });
    }
  };
}

export function useDashboardLayoutValue(uid: string): DashboardLayoutItem[] {
  const { data } = useGetUiSettingsQuery();
  const { isMobile } = useMobile();

  return useMemo(() => {
    if (isMobile) {
      return data?.mobileDashboard?.[uid] ?? [];
    }
    return data?.dashboard?.[uid] ?? [];
  }, [data, uid, isMobile]);
}

export const {
  useGetUiSettingsQuery,
  useSaveTableSettingsMutation,
  useSaveLanguageSettingsMutation,
  useSaveDesktopDashboardLayoutMutation,
  useSaveMobileDashboardLayoutMutation,
} = uiApi;
